import React, { useState, useSyncExternalStore } from 'react';

import { Badge } from '@ergeon/core-components';

import { getRbacDenials, subscribeToRbacDenials } from '@shared/infrastructure/rbacDenials';
import FloatingNotificationBanner from '@shared/ui/FloatingNotificationBanner';
import { useFlashFlag } from '@shared/ui/hooks/useFlashFlag';
import { copyToClipboard } from '@shared/ui/utils/copyToClipboard';

/**
 * Global banner surfacing RBAC denials observed by the fetch watcher. Mounted
 * outside the desktop/mobile apps (portals to document.body), so it works on
 * both — mobile has no NotificationsContextProvider.
 */
export default function RbacDenialBanner() {
  const codes = useSyncExternalStore(subscribeToRbacDenials, getRbacDenials);
  const [dismissedCount, setDismissedCount] = useState(0);
  const { flashed: copied, flash } = useFlashFlag();

  const handleCopy = async() => {
    if (await copyToClipboard(codes.join(', '))) flash();
  };

  const handleDismiss = () => setDismissedCount(codes.length);
  const actionLabel = copied ? 'Copied!' : 'Copy codes';

  return (
    <FloatingNotificationBanner
      actionLabel={actionLabel}
      flavor="danger"
      notificationKey={codes.length}
      onAction={handleCopy}
      onDismiss={handleDismiss}
      show={codes.length > dismissedCount}>
      <span className="flex flex-wrap items-center gap-1" data-testid="rbac-denial-message">
        You do not have permission to view some data on this page. If you need access, contact your
        administrator with these permission codes:
        {codes.map((code) => (
          <Badge flavor="warning" key={code} taste="line" value={code} />
        ))}
      </span>
    </FloatingNotificationBanner>
  );
}
